/**
 * Session-scoped cache + React hook for global system feature flags.
 *
 * - Source of truth: GET /api/public/system-flags (unauthenticated).
 * - All flag consumers share one in-flight promise and cached map —
 *   one network hit per tab, not one per component.
 * - Fails safe: on any network / parse error, all flags default to
 *   enabled (visible). A key that isn't in the returned map also
 *   defaults to enabled — only an explicit `false` hides.
 */
import { useEffect, useState } from 'react';
import { landlordApi } from '../services/api';

type Flags = Record<string, boolean>;

let cached: Flags | null = null;
let inflight: Promise<Flags> | null = null;

const fetchOnce = (): Promise<Flags> => {
    if (cached !== null) return Promise.resolve(cached);
    if (inflight) return inflight;
    inflight = landlordApi.get('api/public/system-flags')
        .then((res: any) => {
            const flags = res?.data?.data?.flags;
            cached = (flags && typeof flags === 'object') ? flags : {};
            return cached as Flags;
        })
        .catch(() => {
            cached = {}; // fail-open — everything visible
            return {};
        })
        .finally(() => { inflight = null; });
    return inflight!;
};

/**
 * Invalidate the session cache — call this after Super Admin toggles a
 * flag so the change reflects on the next render in the same tab.
 */
export const invalidateSystemFlagsCache = () => { cached = null; };

/**
 * Read a single flag. Returns true (visible/enabled) unless the flag
 * is explicitly `false` in the fetched map.
 */
export const useSystemFlag = (key: string): boolean => {
    const [enabled, setEnabled] = useState<boolean>(() => {
        if (!cached) return true;
        return cached[key] !== false;
    });
    useEffect(() => {
        let mounted = true;
        fetchOnce().then((flags) => {
            if (!mounted) return;
            setEnabled(flags[key] !== false);
        });
    }, [key]);
    return enabled;
};
